// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateRangeInput } from '@/components/date-range-input';
import { FilterMenuButton } from '@/components/filter-menu-button';
import { PageContent } from '@/components/page-content';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import { DateRangeShortcut } from '@/constants/date-range-shortcut';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import {
  ProjectCompanyType,
  projectCompanyTypeSchema,
} from '@/constants/project-company-type';
import {
  ProjectStatusSlug,
  projectStatusSlugSchema,
} from '@/constants/project-status';
import {
  Sort,
  SortDirection,
  invert as invertDirection,
  sortDirectionSchema,
} from '@/constants/sort';
import { CustomerGroupedFilterMenu } from '@/customer/components/customer-grouped-filter-menu';
import { ProjectFilter } from '@/filters/project-filter';
import { FulfillmentOrderDialog } from '@/fulfillment/components/fulfillment-order-dialog';
import { FulfillmentOrderFormData } from '@/fulfillment/schemas/fulfillment-order-form-schema';
import { useDisclosure } from '@/hooks/use-disclosure';
import { ItemFilterMenu } from '@/items/components/item-filter-menu';
import { ProjectStatusFilterMenu } from '@/project/components/project-status-filter-menu';
import {
  DEFAULT_FULFILLMENT_BY_SKU_REPORT_STATUSES,
  FULFILLMENT_BY_SKU_REPORT_STATUSES,
  FULFILLMENT_BY_SKU_REPORT_STATUS_LABELS,
  FulfillmentBySkuReportFilter,
  FulfillmentBySkuReportRow,
  FulfillmentBySkuReportSortProperty,
  FulfillmentBySkuReportStatus,
  fulfillmentBySkuReportSortPropertySchema,
  fulfillmentBySkuReportStatusSchema,
} from '@/report/api/types';
import { FulfillmentBySkuReportStatusIcon } from '@/report/components/fulfillment-by-sku-report-status-icon';
import {
  FulfillmentBySkuReportTable,
  FulfillmentBySkuReportTableSkeleton,
} from '@/report/components/fulfillment-by-sku-report-table';
import { FulfillmentReportEducationalTitle } from '@/report/components/fulfillment-report-educational-title';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { serializeDateForAPI } from '@/utilities/date-time';
import {
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { createFileRoute } from '@tanstack/react-router';
import { omit } from 'lodash-es';
import { DateTime } from 'luxon';
import { Suspense, useState } from 'react';
import { z } from 'zod';

type FulfillScope = 'line' | 'project';

const REPORT_TITLE = 'Fulfillment report';
const DEFAULT_SORT_PROPERTY: FulfillmentBySkuReportSortProperty = 'demandStart';
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;

function isDefaultStatusFilter(statuses: FulfillmentBySkuReportStatus[]) {
  return (
    statuses.length === DEFAULT_FULFILLMENT_BY_SKU_REPORT_STATUSES.length &&
    DEFAULT_FULFILLMENT_BY_SKU_REPORT_STATUSES.every((status) =>
      statuses.includes(status)
    )
  );
}

// Seed the fulfillment order form with just the report line's item at the
// quantity still needed. The project comes from `initialProjectID`, which loads
// the full project (and its locations) so the form can auto-fill the rest.
// Passed as `preFillItems` rather than `initialFormData` so the other fields
// still get their defaults (such as the organization default origin location).
function buildFulfillPreFillItems(
  row: FulfillmentBySkuReportRow
): FulfillmentOrderFormData['items'] {
  return [{ item: row.item, quantity: row.netNeedQuantity }];
}

function FilterFallbackButton({ label }: { label: string }) {
  return (
    <FilterMenuButton count={0} isDisabled onClick={() => undefined}>
      {label}
    </FilterMenuButton>
  );
}

const DATE_RANGE_SHORTCUTS: DateRangeShortcut[] = [
  {
    label: 'Next 30 days',
    getDateRange: (today) => [today, today.plus({ days: 30 })],
  },
  {
    label: 'Next 60 days',
    getDateRange: (today) => [today, today.plus({ days: 60 })],
  },
  {
    label: 'Next 90 days',
    getDateRange: (today) => [today, today.plus({ days: 90 })],
  },
  {
    label: 'Overdue',
    getDateRange: (today) => [MINIMUM_DATE, today.minus({ days: 1 })],
  },
];

const searchSchema = z.object({
  statuses: z.array(fulfillmentBySkuReportStatusSchema).optional(),
  projects: z.array(z.string()).optional(),
  companyTypes: z.array(projectCompanyTypeSchema).optional(),
  companies: z.array(z.string()).optional(),
  items: z.array(z.string()).optional(),
  projectStatuses: z.array(projectStatusSlugSchema).optional(),
  needByFrom: z.string().optional(),
  needByTo: z.string().optional(),
  sort: fulfillmentBySkuReportSortPropertySchema.optional(),
  direction: sortDirectionSchema.optional(),
  page: z.number().optional(),
  limit: z.number().optional(),
});

export const Route = createFileRoute('/_app/reports/fulfillment')({
  component: FulfillmentBySkuReportRoute,
  validateSearch: searchSchema,
});

function FulfillmentBySkuReportRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const fulfillDialog = useDisclosure();
  const [fulfillRow, setFulfillRow] =
    useState<FulfillmentBySkuReportRow | null>(null);
  // 'line' seeds the order with just this line's item; 'project' leaves the
  // manifest to the dialog, which auto-fills every item the project still needs
  // from `initialProjectID` — independent of how the report is filtered.
  const [fulfillScope, setFulfillScope] = useState<FulfillScope>('line');

  const statuses = search.statuses ?? [
    ...DEFAULT_FULFILLMENT_BY_SKU_REPORT_STATUSES,
  ];
  const page = search.page ?? DEFAULT_PAGE;
  const pageSize = search.limit ?? DEFAULT_PAGE_SIZE;
  const sort: Sort<FulfillmentBySkuReportSortProperty> = {
    property: search.sort ?? DEFAULT_SORT_PROPERTY,
    direction: search.direction ?? DEFAULT_SORT_DIRECTION,
  };
  const dateRange: DateRange<DateTime> = [
    search.needByFrom ? DateTime.fromISO(search.needByFrom) : null,
    search.needByTo ? DateTime.fromISO(search.needByTo) : null,
  ];

  const filter: FulfillmentBySkuReportFilter = {
    statuses,
    projectIds: search.projects,
    projectStatuses: search.projectStatuses,
    companyTypes: search.companyTypes,
    companyIds: search.companies,
    itemIds: search.items,
    needByFrom: dateRange[0],
    needByTo: dateRange[1],
    pageNumber: page,
    pageSize,
    sort,
  };

  const resetPageSearch = (values: Partial<z.infer<typeof searchSchema>>) => {
    void navigate({
      search: (previous) => ({
        ...omit(previous, 'page'),
        ...values,
      }),
    });
  };

  const handleChangeStatuses = (
    _event: unknown,
    nextStatuses: FulfillmentBySkuReportStatus[]
  ) => {
    if (nextStatuses.length === 0) {
      return;
    }
    if (isDefaultStatusFilter(nextStatuses)) {
      void navigate({
        search: (previous) => omit(previous, ['page', 'statuses']),
      });
      return;
    }
    resetPageSearch({ statuses: nextStatuses });
  };

  const handleChangeProjects = (projects: string[]) => {
    resetPageSearch({ projects: projects.length === 0 ? undefined : projects });
  };

  const handleChangeCompanies = (
    companyTypes: ProjectCompanyType[],
    companies: string[]
  ) => {
    resetPageSearch({
      companyTypes: companyTypes.length === 0 ? undefined : companyTypes,
      companies: companies.length === 0 ? undefined : companies,
    });
  };

  const handleChangeItems = (items: string[]) => {
    resetPageSearch({ items: items.length === 0 ? undefined : items });
  };

  const handleChangeProjectStatuses = (
    projectStatuses: ProjectStatusSlug[]
  ) => {
    resetPageSearch({
      projectStatuses:
        projectStatuses.length === 0 ? undefined : projectStatuses,
    });
  };

  const handleChangeDateRange = (nextDateRange: DateRange<DateTime>) => {
    resetPageSearch({
      needByFrom: serializeDateForAPI(nextDateRange[0]) ?? undefined,
      needByTo: serializeDateForAPI(nextDateRange[1]) ?? undefined,
    });
  };

  const handlePageChange = (nextPage: number) => {
    void navigate({ search: (previous) => ({ ...previous, page: nextPage }) });
  };

  const handleSort = (property: FulfillmentBySkuReportSortProperty) => {
    const direction =
      property === sort.property
        ? invertDirection(sort.direction)
        : DEFAULT_SORT_DIRECTION;

    resetPageSearch({ sort: property, direction });
  };

  const handleClearFilters = () => {
    void navigate({
      search: (previous) => ({
        ...omit(previous, [
          'statuses',
          'projects',
          'companyTypes',
          'companies',
          'items',
          'projectStatuses',
          'needByFrom',
          'needByTo',
          'page',
        ]),
      }),
    });
  };

  const handleRowClick = (row: FulfillmentBySkuReportRow) => {
    void navigate({
      to: '/projects/$projectKey/fulfillment',
      params: { projectKey: row.project.key },
      search: { tab: 'fulfillment-by-sku' },
    });
  };

  const handleFulfill = (row: FulfillmentBySkuReportRow) => {
    setFulfillRow(row);
    setFulfillScope('line');
    fulfillDialog.open();
  };

  const handleFulfillProject = (row: FulfillmentBySkuReportRow) => {
    setFulfillRow(row);
    setFulfillScope('project');
    fulfillDialog.open();
  };

  const hasFilters =
    (search.statuses !== undefined &&
      !isDefaultStatusFilter(search.statuses)) ||
    search.projects !== undefined ||
    search.companyTypes !== undefined ||
    search.companies !== undefined ||
    search.items !== undefined ||
    search.projectStatuses !== undefined ||
    search.needByFrom !== undefined ||
    search.needByTo !== undefined;

  return (
    <PageContent
      sx={{
        gap: 2,
        flex: 1,
        display: 'grid',
        maxHeight: '100%',
        minWidth: 0,
        gridTemplateRows: 'auto auto auto 1fr',
      }}
    >
      <Stack>
        <ReportBreadcrumbs title={REPORT_TITLE} />
        <FulfillmentReportEducationalTitle title={REPORT_TITLE} />
      </Stack>
      <SupportErrorBoundary message="An error occurred while loading the report">
        <Stack
          useFlexGap
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
        >
          <ToggleButtonGroup
            size="small"
            exclusive={false}
            value={statuses}
            onChange={handleChangeStatuses}
            aria-label="Fulfillment status"
          >
            {FULFILLMENT_BY_SKU_REPORT_STATUSES.map((status) => (
              <ToggleButton key={status} value={status}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  paddingX={1}
                >
                  <FulfillmentBySkuReportStatusIcon
                    status={status}
                    fontSize="small"
                  />
                  <Typography variant="button" textTransform="none">
                    {FULFILLMENT_BY_SKU_REPORT_STATUS_LABELS[status]}
                  </Typography>
                </Stack>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <TableControlContainer paddingTop={0}>
          <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            gap={1}
            marginRight="auto"
          >
            <Suspense fallback={<FilterFallbackButton label="Project" />}>
              <ProjectFilter
                label="Project"
                value={search.projects ?? []}
                onChange={handleChangeProjects}
              />
            </Suspense>
            <ProjectStatusFilterMenu
              label="Project status"
              value={search.projectStatuses ?? []}
              onChange={handleChangeProjectStatuses}
            />
            <Suspense fallback={<FilterFallbackButton label="Company" />}>
              <CustomerGroupedFilterMenu
                label="Company"
                companyTypes={search.companyTypes ?? []}
                customerIds={search.companies ?? []}
                includeNotSet={true}
                onChange={handleChangeCompanies}
              />
            </Suspense>
            <Suspense fallback={<FilterFallbackButton label="Item" />}>
              <ItemFilterMenu
                label="Item"
                value={search.items ?? []}
                type="all"
                physicalOnly
                includeArchived
                onChange={handleChangeItems}
              />
            </Suspense>
            {hasFilters && (
              <Button size="small" variant="text" onClick={handleClearFilters}>
                Clear filters
              </Button>
            )}
          </Stack>
          <Stack marginLeft="auto" alignItems="end">
            <DateRangeInput
              isClearable
              width="200px"
              label="Date range"
              value={dateRange}
              shortcuts={DATE_RANGE_SHORTCUTS}
              onChange={handleChangeDateRange}
            />
          </Stack>
        </TableControlContainer>
        <Stack minWidth={0} sx={{ mt: -2 }}>
          <Suspense fallback={<FulfillmentBySkuReportTableSkeleton />}>
            <FulfillmentBySkuReportTable
              filter={filter}
              sort={sort}
              page={page}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onSort={handleSort}
              onRowClick={handleRowClick}
              onFulfill={handleFulfill}
              onFulfillProject={handleFulfillProject}
            />
          </Suspense>
        </Stack>
      </SupportErrorBoundary>
      <FulfillmentOrderDialog
        isOpen={fulfillDialog.isOpen}
        onClose={fulfillDialog.close}
        initialProjectID={fulfillRow?.project.key}
        preFillItems={
          fulfillRow && fulfillScope === 'line'
            ? buildFulfillPreFillItems(fulfillRow)
            : undefined
        }
      />
    </PageContent>
  );
}
